import { z } from "zod";

const specialitySchema = z.object(
  {
    id: z.string(),
    name: z.string().optional(),
  },
  {
    required_error: "page.schedules.newSchedule.steps.search.specialityError",
    invalid_type_error:
      "page.schedules.newSchedule.steps.search.specialityError",
  },
);

const professionalSchema = z.object({
  id: z.string(),
});

const periodSchema = z.object({
  id: z.string(),
  code: z.string(),
});

const examSchema = z.object(
  {
    id: z.string(),
    procedureCode: z.number().optional().nullable(),
    procedureInternalCode: z.number().optional().nullable(),
    procedureOriginCode: z.number().optional().nullable(),
  },
  {
    required_error: "page.schedules.newSchedule.steps.search.examError",
    invalid_type_error: "page.schedules.newSchedule.steps.search.examError",
  },
);

const searchConsult = z.object({
  type: z.literal("3"),
  speciality: specialitySchema,
  professional: professionalSchema.optional().nullable(),
  calendarPreference: z.string().optional().nullable(),
  calendarPreferenceRequired: z.boolean().optional(),
  date: z.date(),
  period: z.array(periodSchema).optional(),
});

const searchExam = z.object({
  type: z.literal("2"),
  examGroup: examSchema.nullable(),
  date: z.date(),
  period: z.array(periodSchema).optional(),
});

export type TSearchExam = z.infer<typeof searchExam>;
export type TSearchConsult = z.infer<typeof searchConsult>;

const searchSchema = z
  .discriminatedUnion("type", [searchConsult, searchExam])
  .refine((val) => val.type, "O tipo de agendamento é obrigatório")
  .superRefine((val, ctx) => {
    if (
      val.type === "3" &&
      val.calendarPreferenceRequired &&
      !val.calendarPreference
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "page.schedules.newSchedule.steps.search.preferenceError",
        path: ["calendarPreference"],
      });
    }
  });

const scheduleSchema = z.object(
  {
    descriptionSite: z.string(),
    identifier: z.number(),
    calendarStartDate: z.string(),
    calendarEndDate: z.string(),
    minutesDuration: z.number(),
    calendarIdentifier: z.number().optional().nullable(),
    insuranceCode: z.number().optional().nullable(),
    procedureCode: z.number().optional().nullable(),
    procedureInternalCode: z.number().optional().nullable(),
    procedureOriginCode: z.number().optional().nullable(),
    calendarPreferencesGrouped: z.string().optional().nullable(),
    calendarAddress: z.string().optional().nullable(),
    orientation: z.string().optional().nullable(),
  },
  { required_error: "page.schedules.newSchedule.steps.schedule.error" },
);

const patientSchema = z.object(
  {
    name: z.string(),
    id: z.string(),
    individualId: z.string().optional(),
  },
  {
    required_error: "page.schedules.newSchedule.steps.patient.error",
    invalid_type_error: "page.schedules.newSchedule.steps.patient.error",
  },
);

const insuranceSchema = z.object(
  {
    id: z.string().min(1),
    name: z.string().min(1, { message: "required" }),
    code: z.string().nullish(),
    insuranceIntegrationId: z.string().nullish(),
    insuranceName: z.string().nullish(),
    categoryIntegrationId: z.string().nullish(),
    categoryName: z.string().nullish(),
    insuranceCategoryCode: z.string().nullish(),
  },
  { required_error: "page.schedules.newSchedule.steps.insurance.error" },
);

const establishmentSchema = z.object(
  {
    id: z.string().min(1),
    name: z.string().min(1),
    integrationId: z.string().min(1),
  },
  { required_error: "page.schedules.newSchedule.steps.establishment.error" },
);
export const appointmentSchema = z.object({
  patient: patientSchema,
  establishment: establishmentSchema,
  insurance: insuranceSchema,
  search: searchSchema,
  schedule: scheduleSchema,
  antecipate: z.boolean(),
  whatsappNumber: z.string().nullish(),
  phoneNumberContact: z.string().min(1, "phone"),
  isWhatsappPhoneNumberContact: z.boolean(),
  scheduleAttachments: z
    .object({
      id: z.string().optional().nullable(),
      files: z.record(z.any()).optional(),
      deletedFilesIds: z.array(z.string()).nullable(),
    })
    .optional(),
});

export type TAppointment = z.infer<typeof appointmentSchema>;
